/**
 * Block 12 PR-B Phase B3 Part C Step 14 + Hotfix-2B Phase B2 Step 4 —
 * computeAutoServiceQty (Audit Round 2 Bug 4 fix).
 *
 * Auto-bind qty для 3 service rows секции Ж (per **прототип 2.12.2:513-520**
 * + Audit Round 2 finalized mapping):
 *   - `'cut-ldsp'` (Распил ЛДСП) → qty из corpus material row (corpusQ)
 *   - `'edge-binding'` (Кромление) → qty из **edge-band** material row (edgeQ)
 *     — hotfix-2B fix: было corpus.qty (методика §1.4.3 OQ-12 trade-off), но
 *     прототип `screen-2.12.2:515` (`autoQ: edgeQ`) + Aibek per Audit Round 2
 *     Bug 4 finalize'ил binding на edge-band материал-row (метры кромки),
 *     не corpus sheets. Service id `'edge-binding'` намеренно отличается от
 *     material id `'edge-band'`, чтобы избежать collision в state.
 *   - `'cut-hdf'` (Распил ХДФ) → qty из hdf material row (hdfQ)
 *
 * Manual rows ('glue'/'gazel'/'porters') — user input, autoBindKey=undefined,
 * этот helper для них не вызывается (Container проверяет `autoBindKey` перед
 * вызовом).
 *
 * **Override propagation:** если user override'ил material qty (через
 * `materialOverrides[corpusId]`), service qty наследует overridden value —
 * мебельщик ожидает «купил 3 листа корпуса, заплачу за распил 3 листов» (per
 * ADR-0020 §Q5). Это поведение реализуется через `MaterialRow.qty` (которое
 * уже учитывает override в Step 13 buildRow).
 *
 * Reference: прототип 2.12.2:513-520; Audit Round 2 «Bug 2/3 finalized per-row
 * mapping»; ADR-0020 §Q5; методика §1.4.3 + OQ-8/10/12.
 */

import type { MaterialRow, ServiceRow } from '../types';

/** Service IDs с auto-bind (3 шт.) — методика §1.4.3. */
export type AutoBindServiceId = 'cut-ldsp' | 'edge-binding' | 'cut-hdf';

/** Sentinel: всё что НЕ AutoBindServiceId — manual input (Склейка/Газель/Грузчики). */
export type ManualServiceId = 'glue' | 'gazel' | 'porters';

export type ServiceId = AutoBindServiceId | ManualServiceId;

/**
 * Возвращает auto-computed qty для service row из material rows.
 *
 * - `'cut-ldsp'` → corpus.qty (corpusQ)
 * - `'edge-binding'` (Кромление) → edge-band.qty (edgeQ) — hotfix-2B Step 4
 *   per прототип 2.12.2:515 + Audit Round 2 Bug 4.
 * - `'cut-hdf'` → hdf.qty (0 если row отсутствует — например BathTabletop
 *   per OQ-7, hdf row пропущен Step 13).
 *
 * **Поиск row.id:**
 *   - corpus → `r.id === 'corpus'`
 *   - edge-band → `r.id === 'edge-band'`
 *   - hdf → `r.id === 'hdf'`
 *
 * Если row не найден (например Балкон не имеет facades) — 0.
 */
export function computeAutoServiceQty(
  serviceId: AutoBindServiceId,
  materialRows: MaterialRow[],
): number {
  switch (serviceId) {
    case 'cut-ldsp':
      return materialRows.find((r) => r.id === 'corpus')?.qty ?? 0;
    case 'edge-binding':
      return materialRows.find((r) => r.id === 'edge-band')?.qty ?? 0;
    case 'cut-hdf':
      return materialRows.find((r) => r.id === 'hdf')?.qty ?? 0;
  }
}

/**
 * Сборка ServiceRow со учётом override + auto-bind.
 *
 * Container (Step 22 SectionServices) использует этот builder для каждого
 * service row, передавая `state.serviceOverrides`, `state.servicePrices`,
 * `state.serviceToggles`. Manual rows (autoBindKey=undefined) — qtyAuto = 0,
 * qty = override ?? 0.
 *
 * Применяется одинаково ко всем 6 service rows; разница между auto/manual —
 * только в значении `qtyAuto` (для manual всегда 0).
 */
export interface ServiceRowSpec {
  id: ServiceId;
  name: string;
  /** Unit для qty display + aria-labels. По умолчанию 'шт' (manual rows). */
  unit?: string;
  autoBindKey?: 'corpusQ' | 'edgeQ' | 'hdfQ';
}

export function buildServiceRow(args: {
  spec: ServiceRowSpec;
  materialRows: MaterialRow[];
  serviceOverrides: Record<string, number>;
  servicePrices: Record<string, number>;
}): ServiceRow {
  const { spec, materialRows, serviceOverrides, servicePrices } = args;
  let qtyAuto = 0;
  if (spec.autoBindKey === 'corpusQ') {
    qtyAuto = computeAutoServiceQty('cut-ldsp', materialRows);
  } else if (spec.autoBindKey === 'edgeQ') {
    qtyAuto = computeAutoServiceQty('edge-binding', materialRows);
  } else if (spec.autoBindKey === 'hdfQ') {
    qtyAuto = computeAutoServiceQty('cut-hdf', materialRows);
  }
  const override = serviceOverrides[spec.id];
  const qtyOverridden = typeof override === 'number';
  const qty = qtyOverridden ? override : qtyAuto;
  const price = servicePrices[spec.id] ?? 0;
  return {
    id: spec.id,
    name: spec.name,
    qty,
    qtyAuto,
    qtyOverridden,
    unit: spec.unit ?? 'шт',
    price,
    cost: qty * price,
    autoBindKey: spec.autoBindKey,
  };
}

/**
 * Канонический список 6 service rows для всех анкет (методика §1.4.3).
 * Порядок: распилы первые (auto-bind), затем услуги доставки/монтажа (manual).
 *
 * НБ: для анкет без ХДФ (только 'bath-tabletop' per OQ-7 — Балкон ХДФ ИМЕЕТ
 * per computeMaterialRows §Q1) service row 'cut-hdf' всё равно рендерится —
 * qtyAuto = 0 (нет hdf material row), мебельщик получит «0 листов ХДФ × цена
 * = 0 ₸», или явно скрыть через UI logic. Phase B4 решит конкретное UX.
 * Сейчас — все 6 service rows для всех anketa.
 */
export const SERVICE_ROW_SPECS: readonly ServiceRowSpec[] = [
  { id: 'cut-ldsp', name: 'Распил ЛДСП', unit: 'лист', autoBindKey: 'corpusQ' },
  { id: 'cut-hdf', name: 'Распил ХДФ', unit: 'лист', autoBindKey: 'hdfQ' },
  // Hotfix-2B Step 4: Кромление autoBindKey=edgeQ + unit='м' per прототип
  // 2.12.2:515 (autoQ: edgeQ, unit: 'м'). Было corpusQ + 'шт' (default) — fix
  // for Audit Round 2 Bug 4.
  { id: 'edge-binding', name: 'Кромление', unit: 'м', autoBindKey: 'edgeQ' },
  { id: 'glue', name: 'Склейка' },
  { id: 'gazel', name: 'Услуги газели', unit: 'час' },
  { id: 'porters', name: 'Услуги грузчиков', unit: 'час' },
];
